'use client'

import { ArrowBigUp, ChevronDown, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import type { RedditSearchComments } from 'sprites-types/workflow/tools/redditSearchComments'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

interface RedditCommentItemProps {
  comment: RedditSearchComments
  depth?: number
  className?: string
}

export function RedditCommentItem({ comment, depth = 0, className }: RedditCommentItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const createdAt = new Date(comment.created_utc * 1000)
  const dateLabel = isNaN(createdAt.getTime())
    ? String(comment.created_utc)
    : createdAt.toLocaleString()
  const textPreview =
    comment.body && comment.body.length > 200 ? comment.body.slice(0, 200) + '…' : comment.body
  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  // Context actions for individual Reddit comments
  const contextActions: ContextAction[] = []

  return (
    <div className={depth === 0 ? '' : 'ml-4 border-l border-gray-200 pl-3'}>
      <ContextMenuWrapper actions={contextActions}>
        <article
          className={`rounded-3xl border border-gray-200/50 bg-white/70 p-4 text-black shadow-sm backdrop-blur-[8px] ${className}`}
        >
          <button
            type="button"
            onClick={toggleExpanded}
            className="w-full text-left"
            aria-expanded={isExpanded}
            aria-controls={`reddit-comment-${dateLabel}-content`}
          >
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="truncate font-semibold">{comment.author}</span>
                </div>
                <div className="mt-0.5 text-xs text-gray-500">{dateLabel}</div>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span
                  title="Upvotes"
                  aria-label="Upvotes"
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200/70 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-rose-50 hover:text-rose-600"
                >
                  <ArrowBigUp className="h-3.5 w-3.5" />
                  <span className="tabular-nums">{comment.score}</span>
                </span>
                <span
                  title="Replies"
                  aria-label="Replies"
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200/70 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span className="tabular-nums">{comment.replies?.length ?? 0}</span>
                </span>
                <ChevronDown
                  className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </div>
            </header>

            {!isExpanded && (
              <div className="mt-2 text-sm text-black">
                {textPreview && <div className="truncate">{textPreview}</div>}
              </div>
            )}
          </button>

          {isExpanded && (
            <div id={`reddit-comment-${dateLabel}-content`} className="mt-3">
              <p className="whitespace-pre-wrap break-words text-sm text-black">{comment.body}</p>
            </div>
          )}
        </article>
      </ContextMenuWrapper>

      {comment.replies && comment.replies.length > 0 && (
        <ul className="mt-2 space-y-2">
          {comment.replies.map((reply, idx) => (
            <li key={idx}>
              <RedditCommentItem comment={reply} depth={depth + 1} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
